import { useMemo, useState } from "react"
import { Box, Button, IconButton, InputAdornment, Stack, TextField, Typography } from "@mui/material"
import { DialogBox } from "lib/@components/dialogBox"
import { Bound } from "lib/@components/binding/Bound"
import { PagedRepeat } from "lib/@components/paged-repeat"
import { If } from "lib/@components/switch"
import { Else } from "lib/@components/else"
import Iconify from "minimals-template/components/Iconify"
import { StandardSwitch } from "lib/@components/binding/bound-components"
import { GroupSelectorDialogItem } from "./group-selector-dialog-item"
import { GroupSelectorSearchItem } from "./group-selector-search-item"

// Virtual top-level buckets matching the schedule tree groupings
const VIRTUAL_ROOTS = [
    { id: "v:1", name: "SFG20", typeId: 1, isVirtual: true },
    { id: "v:3", name: "Specialist Sets", typeId: 3, isVirtual: true },
    { id: "v:4", name: "Functional Sets", typeId: 4, isVirtual: true },
]

export function GroupSelectorDialog({ initialSelection = [], scheduleGroups = [] }) {
    const [navStack, setNavStack] = useState([null])
    const [selected, setSelected] = useState(initialSelection)
    const [search, setSearch] = useState("")
    const [showSelectedOnly, setShowSelectedOnly] = useState(false)

    const currentId = navStack[navStack.length - 1]
    const isSearching = search.trim().length > 0
    const isFlat = isSearching || showSelectedOnly
    const childrenOf = useMemo(buildChildrenMap, [scheduleGroups])
    const searchResults = useMemo(filterGroups, [scheduleGroups, search])
    const selectedGroupsList = useMemo(buildSelectedGroupsList, [scheduleGroups, selected])

    const currentGroup = typeof currentId === "number" ? scheduleGroups.find((g) => g.id === currentId) ?? null : null
    const currentVirtual = typeof currentId === "string" ? VIRTUAL_ROOTS.find((v) => v.id === currentId) ?? null : null
    const currentLabel = isFlat ? null : currentGroup?.name ?? currentVirtual?.name ?? null

    const parentId = navStack.length >= 2 ? navStack[navStack.length - 2] : undefined
    const backLabel =
        parentId == null
            ? "All Groups"
            : VIRTUAL_ROOTS.find((v) => v.id === parentId)?.name ??
              scheduleGroups.find((g) => g.id === parentId)?.name ??
              "Back"

    const currentChildren = childrenOf.get(currentId) ?? []
    const showBackButton = navStack.length > 1 && !isFlat
    const displayList = isSearching ? searchResults : selectedGroupsList
    const emptyMessage = isSearching ? "No groups match your search." : "No groups selected."

    return (
        <DialogBox
            title="Select Schedule Groups"
            value={selected}
            okCaption={selected.length > 0 ? `Select (${selected.length})` : "Select"}
            okDisabled={(v) => !v?.length}
            data-cy="group-selector-dialog"
        >
            <Stack spacing={1} sx={{ minWidth: 380 }}>
                <TextField
                    size="small"
                    placeholder="Search groups…"
                    value={search}
                    onChange={handleSearchChange}
                    fullWidth
                    inputProps={{ "data-cy": "group-selector-search-input" }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Iconify icon="mdi:magnify" width={18} />
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <If truthy={isSearching}>
                                <InputAdornment position="end">
                                    <IconButton aria-label="Clear search" size="small" onClick={clearSearch} edge="end">
                                        <Iconify icon="mdi:close" width={16} />
                                    </IconButton>
                                </InputAdornment>
                            </If>
                        ),
                    }}
                />
                <Stack direction="row" alignItems="center" spacing={1}>
                    <If truthy={showBackButton}>
                        <Button
                            size="small"
                            startIcon={<Iconify icon="mdi:arrow-left" />}
                            onClick={goUp}
                            data-cy="group-selector-back-button"
                        >
                            {backLabel}
                        </Button>
                    </If>
                    <If truthy={currentLabel != null}>
                        <Typography variant="subtitle2" fontWeight={600} sx={{ px: 1 }}>
                            {currentLabel}
                        </Typography>
                    </If>
                    <Box flex={1} />
                    <StandardSwitch
                        value={showSelectedOnly}
                        label={`Show Selected Only${selected.length ? ` (${selected.length})` : ""}`}
                        onChange={setShowSelectedOnly}
                    />
                </Stack>
                <Box sx={{ maxHeight: 380, overflowY: "auto" }}>
                    <Bound childrenOf={childrenOf} selected={selected} onToggle={toggleGroup} onNavigate={navigateInto}>
                        <If truthy={isFlat}>
                            <If truthy={displayList.length > 0}>
                                <PagedRepeat
                                    list={displayList}
                                    item={<GroupSelectorSearchItem />}
                                    keyFn={keyFn}
                                    pageSizeName="group-selector"
                                    showTop
                                />
                                <Else>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ p: 2, textAlign: "center" }}
                                    >
                                        {emptyMessage}
                                    </Typography>
                                </Else>
                            </If>
                            <Else>
                                <If truthy={currentChildren.length > 0}>
                                    <PagedRepeat
                                        list={currentChildren}
                                        item={<GroupSelectorDialogItem />}
                                        keyFn={keyFn}
                                        pageSizeName="group-selector"
                                        showTop
                                    />
                                    <Else>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ p: 2, textAlign: "center" }}
                                        >
                                            No groups in this folder.
                                        </Typography>
                                    </Else>
                                </If>
                            </Else>
                        </If>
                    </Bound>
                </Box>
            </Stack>
        </DialogBox>
    )

    function handleSearchChange(e) {
        setSearch(e.target.value)
    }

    function clearSearch() {
        setSearch("")
    }

    function filterGroups() {
        const term = search.trim().toLowerCase()
        if (!term) return []
        return scheduleGroups.filter((g) => g.name.toLowerCase().includes(term) || g.path.toLowerCase().includes(term))
    }

    function buildSelectedGroupsList() {
        return scheduleGroups.filter((g) => selected.includes(g.id))
    }

    function buildChildrenMap() {
        const map = new Map()

        // Root level shows the virtual type buckets
        map.set(null, VIRTUAL_ROOTS)

        // Each virtual bucket holds root-level real groups of matching type
        for (const vRoot of VIRTUAL_ROOTS) {
            map.set(vRoot.id, [])
        }

        for (const group of scheduleGroups) {
            const pid = group.parentId ?? null
            if (pid === null) {
                // Root-level group: slot into its type's virtual bucket
                const vRoot = VIRTUAL_ROOTS.find((v) => v.typeId === group.typeId)
                if (vRoot) map.get(vRoot.id).push(group)
            } else {
                // Child group: goes under its real parent
                if (!map.has(pid)) map.set(pid, [])
                map.get(pid).push(group)
            }
        }

        return map
    }

    function toggleGroup(id) {
        setSelected((current) => (current.includes(id) ? current.filter((x) => x !== id) : [...current, id]))
    }

    function navigateInto(id) {
        setNavStack((s) => [...s, id])
    }

    function goUp() {
        setNavStack((s) => s.slice(0, -1))
    }
}

function keyFn(item) {
    return item.id
}
